/**
 * La marque de l'entreprise : son logo, ou ses initiales.
 *
 * La barre latérale affichait jusqu'ici une pastille « a1 » écrite en dur —
 * un reste de l'ancien nom, que le renommage vers DKER n'avait pas atteint
 * parce qu'il ne se cherchait pas comme du texte. Elle affiche maintenant le
 * client, ce qui est aussi ce que la pastille était censée faire.
 *
 * Le repli n'est pas décoratif : tant qu'aucun logo n'est déposé — c'est-à-dire
 * pour toute entreprise le jour de son inscription — c'est ce que le patron
 * voit. Des initiales sur sa couleur valent mieux qu'un carré vide.
 */
import { useState } from "react";
import { initialsOf } from "@/lib/branding";
import { cn } from "@/lib/utils";

interface Props {
  name: string | null | undefined;
  logoUrl?: string | null;
  className?: string;
  /** Taille du texte de repli, alignée sur celle de la pastille. */
  textClassName?: string;
}

export function TenantMark({ name, logoUrl, className, textClassName }: Props) {
  // Une URL de logo peut casser sans prévenir : domaine expiré, fichier
  // supprimé, bucket déplacé. Sans ce repli, la barre latérale garderait une
  // icône d'image brisée à demeure.
  const [cassé, setCassé] = useState(false);
  const afficheLogo = Boolean(logoUrl) && !cassé;

  return (
    <div
      className={cn(
        "rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden",
        afficheLogo ? "bg-bg-card" : "bg-accent",
        className,
      )}
    >
      {afficheLogo ? (
        <img
          src={logoUrl ?? undefined}
          alt={name ?? "Logo de l'entreprise"}
          className="w-full h-full object-contain"
          onError={() => setCassé(true)}
        />
      ) : (
        <span
          className={cn(
            "font-mono font-bold text-accent-contrast",
            textClassName ?? "text-sm",
          )}
        >
          {initialsOf(name)}
        </span>
      )}
    </div>
  );
}
